import {AxiosResponse} from 'axios';
import {BackendApiManager} from '../managers/backend-api';
import {Logger} from '../utils/managers/logger';
import {EntityTemplate} from '../backend/responses/entity-template';
import {CarePlan} from '../backend/interfaces/care-plan';
import {CarePlanActivitiesGroupApiRequest} from '../backend/requests/care-plan-activities-group';
import {CarePlanActivitiesGroup} from '../backend/interfaces/care-plan-activities-group';
import {CarePlanActivityTypeApiRequest} from '../backend/requests/care-plan-activity-type';
import {CarePlanActivitiesGroupType} from '../backend/interfaces/care-plan-activities-group-type';
import {SessionDatabase, SessionDatabaseStore} from '../databases/session';
import {MAX_LIMIT_ENTRIES} from '../utils/constants';

export class EntityTemplatesRepository {
  // Singleton Instance
  private static _instance: EntityTemplatesRepository;

  // Private
  private logger: Logger;

  static get instance(): EntityTemplatesRepository {
    if (!this._instance) {
      this._instance = new EntityTemplatesRepository();
    }
    return this._instance;
  }

  constructor() {
    // Create Logger
    this.logger = new Logger('EntityTemplatesRepository');
  }

  public async getTemplates(
    entityId: string,
    activitySectiorId?: string
  ): Promise<EntityTemplate[]> {
    let url: string = `/entities/${entityId}/templates`;

    if (activitySectiorId) {
      url = url + `?activitiesSectorId=${activitySectiorId}`;
    }

    const result: AxiosResponse<EntityTemplate[]> =
      await BackendApiManager.instance.get<EntityTemplate[]>(url);

    return result.data;
  }

  public async getAllTemplates(
    entityId: string,
    activitySectorId?: string
  ): Promise<EntityTemplate[]> {
    let url: string = `/entities/${entityId}/templates?skip=${0}&limit=${MAX_LIMIT_ENTRIES}`;

    if (activitySectorId) {
      url = url + `&activitiesSectorId=${activitySectorId}`;
    }

    // Check Session Database
    const urlCache = await SessionDatabase.instance.getById<Array<EntityTemplate>>(
      SessionDatabaseStore.UrlCache,
      url
    );
    if (!urlCache) {
      // Get from backend
      const response: AxiosResponse<EntityTemplate[]> =
        await BackendApiManager.instance.get<EntityTemplate[]>(url);

      // Save in Session Database
      await SessionDatabase.instance.put(SessionDatabaseStore.UrlCache, url, response.data);

      // Done
      return response.data;
    }

    return urlCache;
  }

  // Create new entity template
  public async createTemplate(
    entityId: string,
    userId: string,
    name: string,
    activitiesSectorId: string,
    activitiesGroupsIds: string[],
    description?: string
  ): Promise<EntityTemplate> {
    let url: string = `/entities/${entityId}/templates`;

    const result: AxiosResponse<EntityTemplate> =
      await BackendApiManager.instance.post<EntityTemplate>(url, {
        name: name,
        description: description,
        cloneFromUserId: userId,
        cloneFromActivitiesSectorId: activitiesSectorId,
        cloneActivitiesGroupsIds: activitiesGroupsIds,
      });

    // clear session cache
    await this.clearTemplatesSessionCache();

    return result.data;
  }

  // Import entity template
  public async importTemplate(
    entityId: string,
    templateId: string,
    userId: string,
    activitiesSectorId: string,
    activitiesGroupsIds: string[]
  ): Promise<CarePlan> {
    let url: string = `/entities/${entityId}/templates/${templateId}/import`;

    const result: AxiosResponse<CarePlan> = await BackendApiManager.instance.put<CarePlan>(url, {
      importToUserId: userId,
      importToActivitiesSectorId: activitiesSectorId,
      importActivitiesGroupsIds: activitiesGroupsIds,
    });

    return result.data;
  }

  // Delete entity template
  public async deleteTemplate(entityId: string, templateId: string): Promise<void> {
    await BackendApiManager.instance.delete<EntityTemplate>(
      `/entities/${entityId}/templates/${templateId}`
    );

    // clear session cache
    await this.clearTemplatesSessionCache();
  }

  // Duplicate entity template
  public async duplicateTemplate(entityId: string, templateId: string): Promise<EntityTemplate> {
    const result: AxiosResponse<EntityTemplate> =
      await BackendApiManager.instance.post<EntityTemplate>(
        `/entities/${entityId}/templates/${templateId}/duplicate`,
        {}
      );

    // clear session cache
    await this.clearTemplatesSessionCache();

    return result.data;
  }

  // Get entity template by id
  public async getTemplate(entityId: string, templateId: string): Promise<EntityTemplate> {
    let url: string = `/entities/${entityId}/templates/${templateId}`;

    const result = await BackendApiManager.instance.get<EntityTemplate>(url);

    return result.data;
  }

  // Update entity template
  public async updateTemplate(
    entityId: string,
    templateId: string,
    newName: string,
    newDescription: string
  ): Promise<EntityTemplate> {
    let url: string = `/entities/${entityId}/templates/${templateId}`;

    const result = await BackendApiManager.instance.patch<EntityTemplate>(url, {
      name: newName,
      description: newDescription,
    });

    // clear session cache
    await this.clearTemplatesSessionCache();

    return result.data;
  }

  // Add entity template activity group
  public async addTemplateActivityGroup(
    entityId: string,
    templateId: string,
    data: CarePlanActivitiesGroupApiRequest
  ): Promise<CarePlanActivitiesGroup> {
    let url: string = `/entities/${entityId}/templates/${templateId}/groups`;

    const response = await BackendApiManager.instance.post<CarePlanActivitiesGroup>(url, data);
    return response.data;
  }

  // Delete entity template activity group
  public async deleteTemplateActivityGroup(
    entityId: string,
    templateId: string,
    activityGroupId: string
  ): Promise<void> {
    let url: string = `/entities/${entityId}/templates/${templateId}/groups/${activityGroupId}`;

    await BackendApiManager.instance.delete(url);
  }

  // Update entity template activity group
  public async updateTemplateActivityGroup(
    entityId: string,
    templateId: string,
    activitiesGroupId: string,
    data: CarePlanActivitiesGroupApiRequest
  ): Promise<CarePlanActivitiesGroup> {
    let url: string = `/entities/${entityId}/templates/${templateId}/groups/${activitiesGroupId}`;

    const response = await BackendApiManager.instance.patch<CarePlanActivitiesGroup>(url, data);
    return response.data;
  }

  // Delete entity template activity group type
  public async deleteTemplateActivityGroupType(
    entityId: string,
    templateId: string,
    activitiesGroupId: string,
    activityTypeEntryId: string
  ): Promise<void> {
    let url: string = `/entities/${entityId}/templates/${templateId}/groups/${activitiesGroupId}/types/${activityTypeEntryId}`;
    await BackendApiManager.instance.delete(url);
  }

  // Update entity template activity group type
  public async updateTemplateActivityGroupType(
    entityId: string,
    templateId: string,
    activitiesGroupId: string,
    activityTypeEntryId: string,
    data: CarePlanActivityTypeApiRequest
  ): Promise<CarePlanActivitiesGroupType> {
    let url: string = `/entities/${entityId}/templates/${templateId}/groups/${activitiesGroupId}/types/${activityTypeEntryId}`;

    const response = await BackendApiManager.instance.patch<CarePlanActivitiesGroupType>(url, data);
    return response.data;
  }

  // Add entity template activity group type
  public async addTemplateActivityGroupType(
    entityId: string,
    templateId: string,
    activitiesGroupId: string,
    data: CarePlanActivityTypeApiRequest
  ): Promise<CarePlanActivitiesGroupType> {
    let url: string = `/entities/${entityId}/templates/${templateId}/groups/${activitiesGroupId}/types`;

    const response = await BackendApiManager.instance.post<CarePlanActivitiesGroupType>(url, data);
    return response.data;
  }

  // Clear all templates session cache
  public async clearTemplatesSessionCache(): Promise<void> {
    await SessionDatabase.instance.removeByMatch(
      SessionDatabaseStore.UrlCache,
      /^\/entities\/.*\/templates/i
    );
  }
}
